import {
  createPackageRedemption,
  getCustomerPackages,
  getPackage,
  getPackageAvailableWeeksByZipCode,
  removePackageRedemption,
} from '@/clients/pops/client'
import { NEW_PROGRAM_WEEK_CAP } from '@/constants/program-dates'
import { $customer } from '../account/stores'
import { Package } from '@/models/package'
import { createPackages } from '@/utilities/factories'
import { setPackages } from './actions'
import { PackageRedemption } from '@/models/package-redemption'
import type { Address } from '@/types/pops'
import { $package, $promises, $redemptions } from './stores'
import type { Dayjs } from '@/utilities/dayjs-config'
import dayjs from '@/utilities/dayjs-config'

const DEFAULT_SUBSCRIPTIONS_PAGE = 1
const DEFAULT_SUBSCRIPTIONS_PER_PAGE = 100

export const loadPackage = async (
  packageId: number,
  force: boolean = false,
) => {
  const promiseKey = `loadPackage`
  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }
  const promise = (async () => {
    const pack = await getPackage(packageId)

    $package.set(new Package(pack))
    const packageRedemptions = pack.pack_order_redemptions.map(
      (redemption) => new PackageRedemption(redemption),
    )
    $redemptions.set(packageRedemptions)
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}

export const loadPackages = async ({
  page = DEFAULT_SUBSCRIPTIONS_PAGE,
  perPage = DEFAULT_SUBSCRIPTIONS_PER_PAGE,
  filterBy,
  force = false,
}: {
  page?: number
  perPage?: number
  filterBy?: string
  force?: boolean
}) => {
  const promiseKey = `loadPackagesPage${page}PerPage${perPage}FilterBy${filterBy}`

  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  const customer = $customer.get()

  if (!customer?.simpleId) {
    return
  }

  const promise = (async () => {
    const rawPackages = await getCustomerPackages(customer.simpleId, {
      page,
      perPage,
      filterBy,
    })

    setPackages({
      packages: createPackages(rawPackages),
      force,
    })
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}

export const createRedemption = async ({
  week,
  address,
}: {
  week: string
  address?: Address
}) => {
  const customer = $customer.get()
  const pack = $package.get()

  if (!customer?.simpleId || !pack?.id) {
    return
  }
  try {
    await createPackageRedemption(pack.id, week, address)
    await loadPackage(pack.id, true)
    return { success: true }
  } catch (error: any) {
    return {
      success: false,
      errorMessage: (error.body?.message || 'An error occurred') as string,
    }
  }
}

export const deleteRedemption = async ({
  redemptionId,
}: {
  redemptionId: number
}) => {
  const customer = $customer.get()
  const pack = $package.get()

  if (!customer?.simpleId || !pack?.id) {
    return { success: false, errorMessage: 'No customer or package found' }
  }

  await removePackageRedemption(pack.id, redemptionId)
  const existingRedemptions = $redemptions.get()
  const newRedemptions = existingRedemptions.filter((redemption) => {
    return redemption.id !== redemptionId
  })

  $redemptions.set(newRedemptions)
  loadPackage(pack.id, true)
}

export const getAvailableWeeksByZipCode = async (
  zipCode: string,
): Promise<Dayjs[]> => {
  const promiseKey = `getAvailableWeeksByZipCode${zipCode}`
  if ($promises.get()[promiseKey]) {
    return $promises.get()[promiseKey]
  }

  const promise = (async () => {
    const weeks = await getPackageAvailableWeeksByZipCode({
      zipCode,
      weekCount: NEW_PROGRAM_WEEK_CAP,
    })

    return weeks.map((week) => dayjs.tz(week))
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}
